/**
 * AdminReportDashboard — admin-only view of all submitted reports.
 * data-testid: candeza-reports-dashboard-*
 */

import React from 'react';
import styled from 'styled-components';
import { useAdminDashboard } from './api';

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  padding-left: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const KpiCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const KpiLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

// Prompt 11E.8: no horizontal-scroll wrapper — same fix idiom used across
// the office-manager report tables. The Bar column also had no width
// floor, so at narrow viewports the ChartBar (percentage-of-td-width)
// visually crowded the adjacent Count number — BarCell gives it a fixed
// minimum column width instead.
const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 420px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  border-collapse: collapse;
  th {
    text-align: left;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 11px;
    text-transform: uppercase;
    padding: 6px 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  td {
    padding: 6px 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  tr:last-child td { border-bottom: none; }
`;

const BarCell = styled.td`
  min-width: 120px;
  white-space: nowrap;
`;

const ChartBar = styled.div<{ width: number }>`
  height: 20px;
  background: ${({ theme }) => theme.colors.primary}44;
  border-radius: 3px;
  width: ${({ width }) => width}%;
  min-width: 4px;
  display: inline-block;
  vertical-align: middle;
`;

export const AdminReportDashboard: React.FC = () => {
  const { data, isLoading, isError } = useAdminDashboard();

  if (isLoading) {
    return <div data-testid="candeza-reports-dashboard-loading">Loading dashboard…</div>;
  }

  if (isError || !data) {
    return (
      <div data-testid="candeza-reports-dashboard-error">Failed to load dashboard.</div>
    );
  }

  const maxMonthly = Math.max(...(data.monthly_trend.map((m) => m.count) || [1]), 1);

  return (
    <div data-testid="candeza-reports-dashboard">
      {/* Totals */}
      <Section>
        <SectionTitle>📊 Overall Totals</SectionTitle>
        <Grid data-testid="candeza-reports-dashboard-totals">
          <KpiCard data-testid="candeza-reports-dashboard-total-submitted">
            <KpiValue>{data.total_submitted}</KpiValue>
            <KpiLabel>Total Submitted</KpiLabel>
          </KpiCard>
          <KpiCard data-testid="candeza-reports-dashboard-contributors">
            <KpiValue>{data.active_contributors}</KpiValue>
            <KpiLabel>Contributors</KpiLabel>
          </KpiCard>
          {Object.entries(data.by_type).map(([type, count]) => (
            <KpiCard key={type} data-testid={`candeza-reports-dashboard-type-${type}`}>
              <KpiValue>{count}</KpiValue>
              <KpiLabel>{type}</KpiLabel>
            </KpiCard>
          ))}
        </Grid>
      </Section>

      {/* 6-month trend chart */}
      {data.monthly_trend.length > 0 && (
        <Section>
          <SectionTitle>📈 6-Month Trend</SectionTitle>
          <div data-testid="candeza-reports-dashboard-trend">
            <TableScroll>
              <Table>
                <thead>
                  <tr><th>Month</th><th>Count</th><th>Bar</th></tr>
                </thead>
                <tbody>
                  {data.monthly_trend.map((m) => (
                    <tr key={m.month}>
                      <td>{m.month}</td>
                      <td>{m.count}</td>
                      <BarCell>
                        <ChartBar width={Math.round((m.count / maxMonthly) * 100)} />
                      </BarCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          </div>
        </Section>
      )}

      {/* Top contributors */}
      {data.top_contributors.length > 0 && (
        <Section>
          <SectionTitle>🏆 Top Contributors</SectionTitle>
          <TableScroll>
            <Table data-testid="candeza-reports-dashboard-leaderboard">
              <thead>
                <tr><th>#</th><th>Name</th><th>Total</th><th>By Type</th></tr>
              </thead>
              <tbody>
                {data.top_contributors.map((c, idx) => (
                  <tr key={c.uid} data-testid={`candeza-reports-dashboard-contributor-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{c.name || c.email}</td>
                    <td>{c.total}</td>
                    <td style={{ fontSize: 11 }}>
                      {Object.entries(c.by_type)
                        .map(([t, n]) => `${t}:${n}`)
                        .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Section>
      )}

      {/* Recent submissions */}
      {data.recent_submissions.length > 0 && (
        <Section>
          <SectionTitle>📋 Recent Submissions</SectionTitle>
          <TableScroll>
            <Table data-testid="candeza-reports-dashboard-recent">
              <thead>
                <tr><th>Type</th><th>Author</th><th>Period</th><th>Submitted</th></tr>
              </thead>
              <tbody>
                {data.recent_submissions.map((r) => (
                  <tr key={r.log_id} data-testid={`candeza-reports-dashboard-recent-row-${r.log_id}`}>
                    <td>{r.report_type}</td>
                    <td>{r.author_name}</td>
                    <td>{r.period_start} – {r.period_end}</td>
                    <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Section>
      )}
    </div>
  );
};

